/**
 * Coupon entity class
 */

import { has } from '../../../app/helpers/object'

/**
 * SubscriptionChannelListingFactory
 * returns SubscriptionChannelListing entity class
 *
 * @class SubscriptionChannelListingFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.SubscriptionChannelListing} class
 *
 * @ngdoc factory
 * @name mwApp.factory:SubscriptionChannelListingFactory
 */
/* @ngInject */
export default function SubscriptionChannelListingFactory ($http, $injector, Entity, m2mMixin, alertLiteSvc) {
  /* === SubscriptionChannelListing class === */

  /**
   * SubscriptionChannelListing model
   *
   * @class SubscriptionChannelListing
   * @classdesc SubscriptionChannelListing model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @return {models.SubscriptionChannelListing} SubscriptionChannelListing entity
   */
  function SubscriptionChannelListing () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  SubscriptionChannelListing.prototype = Object.create(Entity.prototype)
  SubscriptionChannelListing.prototype.constructor = SubscriptionChannelListing

  /* === SubscriptionChannelListing instance methods & fields === */

  Object.assign(SubscriptionChannelListing.prototype, {

    /**
     * instance fields
     *
     * @memberof models.SubscriptionChannelListing#
     * @type {Object}
     */
    _fields: {
      listingType: 'GROUP',
      channels: [{
        id: Number,
        channelNumber: Number,
        name: String
      }],
      groups: [{
        id: Number,
        fromNumber: Number,
        toNumber: Number,
        description: String,
        channels: [{
          id: Number,
          channelNumber: Number,
          name: String
        }]
      }],
      networkId: Number,
      subscriptionId: Number,
      type: String
    },

    /**
     * url template
     *
     * @memberof models.SubscriptionChannelListing#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/channels/{{::type}}',

    /**
     * url template for ungrouped channels
     *
     * @memberof models.SubscriptionChannelListing#
     * @type {String}
     */
    _ungroupedChannelsTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/channels/{{::type}}/extra/ungrouped',

    /**
     * url template for available channels for sequence list
     *
     * @memberof models.SubscriptionChannelListing#
     * @type {String}
     */
    _availableChannelsTemplate: 'api//networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/channels/{{::type}}/extra/available'
  })

  /* === SubscriptionChannelListing static methods & fields === */

  /**
   * Generate url
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {String} [url]
   * @return {String}
   */
  SubscriptionChannelListing.generateUrl = function (url = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    return `${cast.prototype.$_urlTemplate(o)}` + url
  }

  /**
   * Get assigned shows
   *
   * @memberof models.SubscriptionChannelListing#
   */
  SubscriptionChannelListing.getChannelList = function (o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    const url = cast.prototype.$_availableChannelsTemplate(o)

    return $http.get(url)
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get assigned shows
   *
   * @memberof models.SubscriptionChannelListing#
   */
  SubscriptionChannelListing.getUngroupedChannels = function (o) {
    o = o || {}
    const cast = $injector.get(this._name) // get constructor from injector by name
    const url = cast.prototype.$_ungroupedChannelsTemplate(o)

    return $http.get(url)
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }
  /**
   * update subcategory, performing PUT/POST request to backend
   *
   * @memberof models.SubscriptionChannelListing
   * @override
   */
  SubscriptionChannelListing.save = function (cl) {
    const data = angular.copy(cl)
    if (data.listingType === 'SEQUENCE') {
      data.channels.forEach((ch, index) => {
        ch.channelNumber = index + 1
      })
    }
    data.groups.forEach((g) => {
      g.channels.forEach((ch, index) => {
        ch.channelNumber = index + g.fromNumber
      })
    })
    const cast = $injector.get(this._name) // get constructor from injector by name
    return $http({
      method: 'PUT',
      url: cast.prototype.$_urlTemplate(data),
      data
    }).then(
      response => {
        alertLiteSvc.alert('Channel Listing is saved', 'success')

        const temp = has('payload')(response.data)
          ? response.data.payload
          : response.data
        if (temp) {
          this._weakCopy(temp)
        }
        if (data.listingType === 'SEQUENCE') {
          cl.channels = data.channels
        } else {
          cl.groups = data.groups
        }

        return response
      }
    ).catch(alertLiteSvc.alertError)
  }

  // copy all static methods from Entity class
  Object.assign(SubscriptionChannelListing, Entity)
  SubscriptionChannelListing._name = 'SubscriptionChannelListing'
  SubscriptionChannelListing._publicName = 'Channel List'
  SubscriptionChannelListing.init()

  // permissions
  SubscriptionChannelListing.access = {
    view: '',
    create: '',
    edit: '',
    delete: ''
  }

  return SubscriptionChannelListing
}
